import React, { Component, ReactNode } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

interface Props extends WithTranslation {
    children: ReactNode;
    chartName?: string;
}

interface State {
    hasError: boolean;
    error?: Error;
}

/**
 * ChartErrorBoundary - Catches errors in chart components
 * Prevents individual chart errors from crashing the entire board
 */
class ChartErrorBoundaryComponent extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error(`Chart error in ${this.props.chartName || 'unknown chart'}:`, error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="w-full h-full flex items-center justify-center bg-transparent">
                    <div className="text-center p-6 max-w-md">
                        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                            <span className="w-6 h-6 text-red-600" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_55954675.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_55954675.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                        </div>
                        <h3 className="text-gray-900 font-semibold text-[11px] mb-2">{this.props.t('charts.components.unableToDisplay')}</h3>
                        <p className="text-gray-600 text-[10px] mb-4">
                            {this.props.t('charts.components.chartLoadError')}
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white text-[11px] rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            {this.props.t('charts.components.reloadPage')}
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export const ChartErrorBoundary = withTranslation('common')(ChartErrorBoundaryComponent);
